import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Pencil, Trash2, Plus, X, Upload } from "lucide-react";

const empty = { title: "", cover_image: "", images: [], project_id: "", event_id: "", consent_verified: false, published: false };

export default function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/galleries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title) return toast.error("Titolo obbligatorio");
    if (editing.published && !editing.consent_verified) return toast.error("Per pubblicare conferma il consenso/liberatoria");
    try {
      if (editing.id) await api.put(`/admin/galleries/${editing.id}`, editing);
      else await api.post("/admin/galleries", editing);
      toast.success("Salvato"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => {
    if (!confirm("Eliminare?")) return;
    await api.delete(`/admin/galleries/${id}`);
    toast.success("Eliminato"); load();
  };

  const uploadMulti = async (files) => {
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: data.path, alt: file.name, caption: "" });
      } catch { toast.error(`Upload fallito: ${file.name}`); }
    }
    setEditing((s) => ({ ...s, images: [...(s.images || []), ...uploaded], cover_image: s.cover_image || uploaded[0]?.url || "" }));
    setUploading(false);
  };

  const input = "w-full rounded-xl bg-white border border-[#292522]/12 px-3 py-2 focus:border-[#FF6100] focus:ring-2 focus:ring-[#FF6100]/20 outline-none text-sm";

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-end justify-between mb-6">
        <div><h1 className="font-display text-4xl font-medium">Gallery</h1><p className="text-sm text-[#292522]/60 mt-1">Album collegati a eventi e progetti. Sempre con consenso verificato.</p></div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary text-sm"><Plus className="h-4 w-4" />Nuova gallery</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((g) => (
          <div key={g.id} className="card-vv bg-white p-5">
            <div className="aspect-video rounded-xl overflow-hidden bg-[#FFF1E8] mb-3">
              {g.cover_image ? <img src={fileUrl(g.cover_image)} alt={g.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#292522]/40 text-xs">Nessuna cover</div>}
            </div>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg font-medium">{g.title}</h3>
                <p className="text-xs text-[#292522]/50">{g.images?.length || 0} foto · {g.published ? "pubblicata" : "bozza"} · {g.consent_verified ? "consenso ✓" : "consenso ✗"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing({ ...empty, ...g })} className="p-1.5 hover:text-[#FF6100]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(g.id)} className="p-1.5 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#292522]/50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-[#292522]/8">
              <h2 className="font-display text-2xl font-medium">{editing.id ? "Modifica" : "Nuova"} gallery</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <label><span className="text-xs font-semibold">Titolo *</span><input className={input} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></label>
              <div>
                <label className="btn-ghost text-sm inline-flex cursor-pointer">
                  <Upload className="h-4 w-4" strokeWidth={1.75} /> {uploading ? "Caricamento…" : "Aggiungi immagini"}
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => uploadMulti(Array.from(e.target.files))} />
                </label>
              </div>
              {editing.images?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {editing.images.map((img, i) => (
                    <div key={i} className="relative">
                      <img src={fileUrl(img.url)} alt={img.alt} className="aspect-square w-full object-cover rounded-lg" />
                      <button onClick={() => setEditing((s) => ({ ...s, images: s.images.filter((_, j) => j !== i) }))} className="absolute top-1 right-1 bg-white rounded-full p-1"><X className="h-3 w-3" /></button>
                      <button onClick={() => setEditing((s) => ({ ...s, cover_image: img.url }))} className={`absolute bottom-1 left-1 rounded px-1.5 py-0.5 text-[10px] ${editing.cover_image === img.url ? "bg-[#FF6100] text-white" : "bg-white/90"}`}>Cover</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-xl bg-[#FFF1E8] p-4">
                <label className="flex gap-3 items-start cursor-pointer">
                  <input type="checkbox" checked={editing.consent_verified} onChange={(e) => setEditing({ ...editing, consent_verified: e.target.checked })} className="mt-1 accent-[#FF6100]" data-testid="consent-checkbox" />
                  <span className="text-sm"><strong>Consenso o liberatoria alla pubblicazione verificato</strong><br /><span className="text-xs text-[#292522]/60">Obbligatorio prima di pubblicare (attenzione ai minori)</span></span>
                </label>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-[#FF6100]" /><span className="text-xs font-semibold">Pubblica sulla galleria pubblica</span></label>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm">Annulla</button>
              <button onClick={save} className="btn-primary text-sm">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
